import React from 'react';
import { Icons } from './Icons';
import AdUnit from './AdUnit';
import BackButton from './BackButton';

interface HelpPageProps {
  onBack: () => void;
}

const HelpPage: React.FC<HelpPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 transition-colors duration-200">
      <div className="max-w-4xl mx-auto px-4 py-12 pb-32">
        <BackButton onClick={onBack} />

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Help & Documentation
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Everything you need to know about using TF Override Generator.
          </p>
        </div>

        <div className="space-y-12">
          {/* How To Use */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 mr-4">
                <Icons.List className="w-5 h-5" />
              </span>
              How to Use
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: 'Select IaC Tool',
                  desc: 'Choose between Terraform, Terragrunt, or OpenTofu depending on your stack.',
                },
                {
                  step: 2,
                  title: 'Choose cloud provider and region',
                  desc: 'Select AWS, Azure, GCP, or OCI and your target deployment region.',
                },
                {
                  step: 3,
                  title: 'Enter module URL',
                  desc: 'Provide a public GitHub URL for the Terraform module you wish to use.',
                },
                {
                  step: 4,
                  title: 'Analyze Module',
                  desc: 'Click "Analyze" to let the AI fetch and parse the module\'s variable definitions.',
                },
                {
                  step: 5,
                  title: 'Configure Variables',
                  desc: 'Select variables from the dropdown and enter your desired override values.',
                },
                {
                  step: 6,
                  title: 'Stage Overrides',
                  desc: 'Click "Add Variable" to add your configuration to the staging list.',
                },
                {
                  step: 7,
                  title: 'Generate Files',
                  desc: 'Click "Generate Files" to produce the final HCL code, then copy or download the results.',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 rounded-full flex items-center justify-center font-bold text-sm border">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{item.title}</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Ad Unit: Middle */}
          <AdUnit slotId="5555555555" format="horizontal" />

          {/* Video */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mr-4">
                <Icons.Video className="w-5 h-5" />
              </span>
              Video Tutorial
            </h2>
            <div className="relative w-full pb-[56.25%] rounded-xl overflow-hidden bg-black shadow-lg">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/rNBikJdFY1E"
                title="TF Override Generator Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </section>

          {/* Regions & Modules Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Regions */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center">
                <Icons.Globe className="w-5 h-5 text-slate-400 mr-3" />
                Cloud Regions
              </h2>
              <ul className="space-y-4">
                {[
                  {
                    name: 'AWS Regions',
                    url: 'https://docs.aws.amazon.com/global-infrastructure/latest/regions/aws-regions.html',
                    color: 'hover:text-[#FF9900]',
                  },
                  {
                    name: 'Azure Regions',
                    url: 'https://github.com/claranet/terraform-azurerm-regions/blob/master/REGIONS.md',
                    color: 'hover:text-[#0078D4]',
                  },
                  {
                    name: 'GCP Regions',
                    url: 'https://cloud.google.com/compute/docs/regions-zones#available',
                    color: 'hover:text-[#4285F4]',
                  },
                  {
                    name: 'OCI Regions',
                    url: 'https://www.oracle.com/cloud/public-cloud-regions/',
                    color: 'hover:text-[#C74634]',
                  },
                ].map((region) => (
                  <li key={region.name}>
                    <a
                      href={region.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-all"
                    >
                      <span
                        className={`flex items-center text-slate-700 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors`}
                      >
                        {region.name}
                      </span>
                      <Icons.ExternalLink className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {/* Modules */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center">
                <Icons.Package className="w-5 h-5 text-slate-400 mr-3" />
                Module Repositories
              </h2>
              <ul className="space-y-4">
                {[
                  {
                    name: 'AWS Modules',
                    url: 'https://github.com/orgs/terraform-aws-modules/repositories',
                  },
                  { name: 'Azure Modules', url: 'https://github.com/orgs/Azure/repositories' },
                  {
                    name: 'Google Modules',
                    url: 'https://github.com/orgs/terraform-google-modules/repositories',
                  },
                  {
                    name: 'Oracle Modules',
                    url: 'https://github.com/orgs/oracle-terraform-modules/repositories',
                  },
                ].map((mod) => (
                  <li key={mod.name}>
                    <a
                      href={mod.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-all"
                    >
                      <span className="flex items-center text-slate-700 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {mod.name}
                      </span>
                      <Icons.ExternalLink className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Ad Unit: Bottom */}
          <AdUnit slotId="6666666666" format="auto" />

          {/* VSCode & Contact */}
          <div className="grid md:grid-cols-2 gap-8">
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <Icons.Monitor className="w-5 h-5 text-blue-500 mr-3" />
                VSCode Extension
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">
                Enhance your workflow directly in your editor with our official extension.
              </p>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mb-6 text-sm border border-slate-100 dark:border-slate-700">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500 dark:text-slate-400">Name:</span>
                  <span className="font-mono text-slate-900 dark:text-white font-semibold">
                    tfverride
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Publisher:</span>
                  <span className="font-mono text-slate-900 dark:text-white font-semibold">
                    senet
                  </span>
                </div>
              </div>
              <div className="mt-auto">
                <a
                  href="https://marketplace.visualstudio.com/items?itemName=senet.tfoverrides"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full justify-center items-center px-4 py-3 bg-[#007ACC] hover:bg-[#0065A9] text-white rounded-lg font-medium transition-colors shadow-sm"
                >
                  <Icons.ExternalLink className="w-4 h-4 mr-2" /> View on Marketplace
                </a>
              </div>
            </section>

            <section className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <Icons.Headphones className="w-5 h-5 text-indigo-500 mr-3" />
                Enterprise Support
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                Need help with enterprise integration, custom features, or dedicated support?
              </p>

              <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                  <Icons.Mail className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <a
                    href="mailto:help@tfoverrides.dev"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline break-all"
                  >
                    help@tfoverrides.dev
                  </a>
                </div>
              </div>

              <div className="mt-auto space-y-3">
                <a
                  href="https://calendly.com/sen-net/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full justify-center items-center px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                >
                  <Icons.Video className="w-4 h-4 mr-2" />
                  Book a Consultation
                </a>
                <a
                  href="mailto:help@tfoverrides.dev?subject=Enterprise Support Inquiry"
                  className="inline-flex w-full justify-center items-center px-4 py-3 bg-white dark:bg-slate-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors"
                >
                  <Icons.Mail className="w-4 h-4 mr-2" />
                  Email Us
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
